import { z } from 'zod'

export type JsonScalar = string | number | boolean | null
export type JsonValue = JsonScalar | JsonObject | JsonValue[]
export interface JsonObject {
  readonly [key: string]: JsonValue
}

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
)

export const IssueAttributeValueJsonSchema = z.object({
  checkbox: z.boolean().optional(),
  number: z.union([z.number(), z.string()]).optional(),
})
export type IssueAttributeValueJson = z.infer<typeof IssueAttributeValueJsonSchema>

export function parseIssueAttributeValueJson(value: unknown): IssueAttributeValueJson {
  let candidate = value
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value) as unknown
    } catch {
      return {}
    }
  }

  return IssueAttributeValueJsonSchema.safeParse(candidate).data ?? {}
}

export const IssueHistoryAttributeMetadataSchema = z
  .object({
    attribute_id: z.number().optional(),
    attribute_label: z.string().optional(),
    attribute_slug: z.string().optional(),
    data_type: JsonValueSchema.optional(),
    value: JsonValueSchema.optional(),
    previous_value: JsonValueSchema.optional(),
    changed: z.boolean().optional(),
    reasoning: z.string().optional(),
    previous_source: z.string().optional(),
  })
  .catchall(JsonValueSchema)
export type IssueHistoryAttributeMetadata = z.infer<typeof IssueHistoryAttributeMetadataSchema>

export const IssueHistoryMetadataSchema = z
  .object({
    attributes: z.array(IssueHistoryAttributeMetadataSchema).optional(),
    reasoning: z.string().optional(),
    original_description: z.string().optional(),
    suggested_issue_type_name: z.string().optional(),
  })
  .catchall(JsonValueSchema)
export type IssueHistoryMetadata = z.infer<typeof IssueHistoryMetadataSchema>

/** Legacy rows can contain JSON strings or partially invalid metadata. */
export function parseIssueHistoryMetadata(value: unknown): IssueHistoryMetadata | null {
  let candidate = value
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value) as unknown
    } catch {
      return null
    }
  }

  return IssueHistoryMetadataSchema.safeParse(candidate).data ?? null
}
